import { useEffect, useState } from "react";
import { FileCode2, FlaskConical, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, x12Date, fmtNum } from "@/lib/api";
import { PageHeader, Panel, Th, Td, Empty, btn, field } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { EdiImportDialog } from "@/components/EdiImportDialog";
import { RawEdiDialog } from "@/components/RawEdiDialog";
import { PaperStockPanel } from "@/components/PaperStockPanel";
import { ReelReportPanel } from "@/components/ReelReportPanel";

const Sel = ({ value, onChange, options, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={`h-9 ${field}`} data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
    <SelectContent className="border-slate-700 bg-[#252A35] text-slate-100">
      {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-opt-${v}`}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const CreateDialog = ({ open, onOpenChange, items, sites, stock, onDone }) => {
  const [site, setSite] = useState("");
  const [date, setDate] = useState("");
  const [sapPo, setSapPo] = useState("");
  const [lines, setLines] = useState([{ part_no: "", qty: "" }]);
  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = async () => {
    try {
      await api.post("/calloffs", { site_code: site, sap_po: sapPo, required_date: date, lines: lines.map((l) => ({ part_no: l.part_no, qty: Number(l.qty) })) });
      toast.success("Call-off created — 940 Warehouse Shipping Order sent to Group O");
      onOpenChange(false);
      setSapPo("");
      setLines([{ part_no: "", qty: "" }]);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-800 bg-[#1F232C] text-slate-100" data-testid="calloff-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New raw material call-off</DialogTitle>
          <DialogDescription className="text-slate-400">Enter the SAP PO raised direct to Mondi. A 940 Warehouse Shipping Order for the same quantity is sent to Group O immediately; Group O returns a 997.</DialogDescription>
        </DialogHeader>
        <Input value={sapPo} onChange={(e) => setSapPo(e.target.value)} placeholder="SAP PO number (raised to Mondi)" className={field} data-testid="calloff-sap-po" />
        <div className="grid gap-3 sm:grid-cols-2">
          <Sel value={site} onChange={setSite} options={sites.filter((s) => s.code !== "EP").map((s) => [s.code, `${s.code} · ${s.city}`])} testId="calloff-site" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} data-testid="calloff-date" />
        </div>
        {lines.map((l, i) => {
          const avail = stock[l.part_no]?.sites?.[site]?.on_hand || 0;
          const over = site && l.part_no && Number(l.qty) > avail;
          return (
          <div key={i}>
          <div className="flex gap-2">
            <div className="flex-1"><Sel value={l.part_no} onChange={(v) => setLine(i, "part_no", v)} options={items.map((x) => [x.part_no, `${x.part_no} · ${x.description}`])} testId={`calloff-line-part-${i}`} /></div>
            <Input type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} placeholder="Qty" className={`w-28 ${field}`} data-testid={`calloff-line-qty-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="text-slate-400 hover:bg-slate-800 hover:text-white" data-testid={`calloff-line-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
          {over && <div className="mt-1 text-xs text-amber-300" data-testid={`calloff-line-warning-${i}`}>Only {fmtNum(avail)} {items.find((x) => x.part_no === l.part_no)?.uom || ""} on hand at {site} - the call-off can still be sent.</div>}
          </div>
          );
        })}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => setLines([...lines, { part_no: "", qty: "" }])} className={btn.ghost} data-testid="calloff-add-line-btn"><Plus className="mr-1 h-4 w-4" /> Line</Button>
          <Button onClick={save} className={btn.primary} data-testid="calloff-save-btn">Create call-off</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function RawMaterials() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [create, setCreate] = useState(false);
  const [imp, setImp] = useState({ open: false, raw: "" });
  const [view, setView] = useState(null);
  const [fg, setFg] = useState([]);
  const [stock, setStock] = useState({});
  const loadStock = () => api.get("/inventory").then((r) => setStock(Object.fromEntries(r.data.rows.filter((x) => x.type === "RM").map((x) => [x.part_no, x]))));
  const load = () => api.get("/calloffs").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    loadStock();
    api.get("/items").then((r) => {
      setItems(r.data.filter((i) => i.type === "RM"));
      setFg(r.data.filter((i) => i.type === "FG"));
    });
    api.get("/sites").then((r) => setSites(r.data));
  }, []);
  const simulate = async (c) => setImp({ open: true, raw: (await api.get(`/calloffs/${c.id}/test-response`)).data.raw });

  return (
    <div>
      <PageHeader title="Raw materials at Group O" subtitle="EP raises the SAP PO direct to Mondi → Mondi ships neutral bulk stock to Group O → EP sends a 940 Warehouse Shipping Order (same qty) → Group O returns 997 → Group O picks, scans Mondi reel IDs and sends a 945 → Hub returns 997.">
        <Button variant="ghost" onClick={() => setImp({ open: true, raw: "" })} className={btn.ghost} data-testid="rm-import-945-btn">Import 945 Shipping Advice</Button>
        <Button onClick={() => setCreate(true)} className={btn.primary} data-testid="rm-new-calloff-btn"><Plus className="mr-1.5 h-4 w-4" /> New call-off</Button>
      </PageHeader>
      <PaperStockPanel reels={Object.values(stock)} fg={fg} onChanged={loadStock} />
      <Panel title="Call-offs">
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="calloffs-table">
              <thead className="border-b border-slate-800"><tr><Th>Call-off</Th><Th>SAP PO</Th><Th>From</Th><Th>Lines</Th><Th>Received</Th><Th>Reels</Th><Th>Required</Th><Th>Status</Th><Th></Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`calloff-row-${c.calloff_no}`}>
                    <Td className="font-mono text-slate-100">{c.calloff_no}</Td>
                    <Td className="font-mono" data-testid={`calloff-sap-po-${c.calloff_no}`}>{c.sap_po || "—"}</Td>
                    <Td className="font-mono">{c.site_code}</Td>
                    <Td className="font-mono">{c.lines.map((l) => <div key={l.part_no}>{l.part_no} × {fmtNum(l.qty)} {l.uom}</div>)}</Td>
                    <Td className="font-mono">{c.lines.map((l) => <div key={l.part_no}>{fmtNum(l.received_qty)}</div>)}</Td>
                    <Td className="max-w-[220px] font-mono text-[10px]" data-testid={`calloff-reels-${c.calloff_no}`}>{c.lines.flatMap((l) => l.reels || []).join(", ") || "—"}</Td>
                    <Td>{x12Date(c.required_date)}</Td>
                    <Td><StatusBadge status={c.status} /></Td>
                    <Td className="whitespace-nowrap text-right">
                      {(c.edi_940_id || c.edi_850_id) && <Button size="sm" variant="ghost" onClick={() => setView(c.edi_940_id || c.edi_850_id)} className={btn.ghost} data-testid={`calloff-view-940-${c.calloff_no}`}><FileCode2 className="h-3.5 w-3.5" /></Button>}
                      {["sent", "partially_received"].includes(c.status) && (
                        <Button size="sm" variant="ghost" onClick={() => simulate(c)} className={`ml-1 ${btn.ghost}`} data-testid={`calloff-simulate-945-${c.calloff_no}`}><FlaskConical className="mr-1 h-3.5 w-3.5" /> Test 945 Shipping Advice</Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty testId="calloffs-empty">No call-offs yet.</Empty>}
      </Panel>
      <ReelReportPanel refreshKey={rows} />
      <CreateDialog open={create} onOpenChange={setCreate} items={items} sites={sites} stock={stock} onDone={() => { load(); loadStock(); }} />
      <EdiImportDialog open={imp.open} initialRaw={imp.raw} onOpenChange={(v) => setImp({ ...imp, open: v })} onDone={() => { load(); loadStock(); }} />
      <RawEdiDialog open={!!view} messageId={view} onOpenChange={(v) => !v && setView(null)} />
    </div>
  );
}
